import React, { useState, useEffect, useCallback, useMemo, useContext, createContext } from 'react';
import { fetchPriorityQueue } from '../../../utils/api';
import { apiFetch } from '../../../services/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import { useConsumerUnit } from '../../../contexts/ConsumerUnitContext';
import { useEventStream } from '../../../hooks/useEventStream';

const QueueContext = createContext(null);

/** Today's date as YYYY-MM-DD for comparison */
/** Local date as YYYY-MM-DD (not UTC — avoids timezone shift after ~8PM Eastern) */
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Derive view from dueDate alone — no bucket field needed.
 *   Today      = dueDate exists AND dueDate <= today
 *   Scheduled  = dueDate exists AND dueDate > today
 *   Parking    = no dueDate (null/undefined/empty)
 */
function classifyItem(item) {
  const d = item.dueDate;
  if (!d) return 'parking';
  const dateOnly = d.slice(0, 10);
  const today = todayStr();
  if (dateOnly < today) return 'overdue';
  return dateOnly === today ? 'today' : 'scheduled';
}

/**
 * Provider — wraps InboxShell so all tabs + badge share the same queue state.
 * Single fetch, single items array, single set of derived views.
 */
export function QueueProvider({ children }) {
  const { getToken } = useAuth();
  const { selectedUnitId } = useConsumerUnit();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const authFetch = useCallback((path, opts) => apiFetch(path, opts, getToken), [getToken]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchPriorityQueue(selectedUnitId, {}, getToken).then((data) => {
      if (Array.isArray(data)) setItems(data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [getToken, selectedUnitId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh on queue changes via SSE (replaces CustomEvent listener)
  const { subscribe } = useEventStream();
  useEffect(() => {
    const unsub = subscribe('queue.changed', () => refresh());
    // Also keep the CustomEvent fallback for local-only mutations (e.g. email actions)
    const handler = () => refresh();
    window.addEventListener('nexonurse:queue-changed', handler);
    return () => {
      unsub();
      window.removeEventListener('nexonurse:queue-changed', handler);
    };
  }, [subscribe, refresh]);

  // Filter out completed/resolved items — only show active queue items.
  const activeItems = useMemo(() => items
    .filter((i) => !i.status || i.status === 'pending' || i.status === 'in_progress' || i.status === 'blocked'),
  [items]);

  // Derive views from dueDate — no bucket field used.
  const todayItems = useMemo(() => activeItems.filter((i) => { const c = classifyItem(i); return c === 'today' || c === 'overdue'; }), [activeItems]);
  const scheduledItems = useMemo(() => activeItems.filter((i) => classifyItem(i) === 'scheduled'), [activeItems]);
  const parkedItems = useMemo(() => activeItems.filter((i) => classifyItem(i) === 'parking'), [activeItems]);

  // Move to "today" = set dueDate to today. "scheduled" = set dueDate to future. "parking" = clear dueDate.
  // No event dispatch — optimistic setItems is sufficient. Refresh only on failure.
  const moveToBucket = useCallback(async (id, targetView, dueDate) => {
    const newDueDate = targetView === 'parking' ? null : (dueDate || todayStr());
    setItems((prev) => prev.map((item) => item.id === id ? { ...item, dueDate: newDueDate } : item));
    authFetch(`/api/queue/${id}/bucket`, {
      method: 'PUT',
      body: JSON.stringify({ bucket: targetView, dueDate: newDueDate }),
    }).catch(() => { refresh(); });
  }, [authFetch, refresh]);

  const completeItem = useCallback(async (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    authFetch(`/api/queue/${id}/resolve`, { method: 'POST' }).catch(() => { refresh(); });
  }, [authFetch, refresh]);

  const removeItem = useCallback(async (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    authFetch(`/api/queue/${id}`, { method: 'DELETE' }).catch(() => { refresh(); });
  }, [authFetch, refresh]);

  const value = useMemo(() => ({
    items: activeItems, todayItems, scheduledItems, parkedItems, loading, refresh, moveToBucket, completeItem, removeItem,
  }), [activeItems, todayItems, scheduledItems, parkedItems, loading, refresh, moveToBucket, completeItem, removeItem]);

  return React.createElement(QueueContext.Provider, { value }, children);
}

/**
 * Hook — all tabs call this to get the shared queue state.
 * Must be used inside QueueProvider.
 */
export function useQueueItems() {
  const ctx = useContext(QueueContext);
  if (!ctx) throw new Error('useQueueItems must be used within QueueProvider');
  return ctx;
}
